// ----------------------------------------------------------------------
// <copyright file="Izenda.GrandTotalGrid.js" company="Izenda">
// Copyright (c) 2015 Izenda, Inc.  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and  International
// Copyright Laws. Unauthorized reproduction,  reverse-engineering, and
// distribution of all or any portion of  the code contained in this file is
// strictly prohibited and may  result in severe civil and criminal penalties
// and will be  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES  ARE CONFIDENTIAL AND
// PROPRIETARY TRADE  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
// LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET  CONTROLS AS
// PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED  FILES OR ANY
// PORTION OF ITS CONTENTS SHALL AT NO TIME BE  COPIED, TRANSFERRED, SOLD,
// DISTRIBUTED, OR OTHERWISE MADE  AVAILABLE TO OTHER INDIVIDUALS WITHOUT
// EXPRESS WRITTEN CONSENT  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON  ADDITIONAL
// RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

/**
 * GrandTotalGrid component
 */
import React, { Component, PropTypes } from 'react';
import AppGlobal from 'Izenda.AppGlobal';
import { observer } from 'mobx-react';
import classnames from 'classnames';
import constants from 'logic/data/Izenda.ReportPartGridConstants';
import calFieldConstants from 'logic/data/Izenda.CalculatedFieldConstants.js';
import ReportPartGrandTotalGrid from './ReportPartGrid/Izenda.ReportPartGrandTotalGrid';
import GridLayout from 'share/components/controls/Izenda.GridLayout';
import convertToNumeralFormat from 'share/utils/Izenda.ConvertToNumeralFormat';
import convertToDateTimeFormat from 'share/utils/Izenda.ConvertToDateTimeFormat';
import localizer from 'share/localization/Izenda.Localizer';
import PrintingUtils from 'share/utils/Izenda.PrintingUtils';
import { applyFormater as applyFormatter } from 'share/utils/Izenda.ReportPartUtils';
import { forEach, findIndex, isEmpty, isNil, find, get } from 'lodash/fp';

@observer
export default class GrandTotalGrid extends Component {
  static propTypes = {
    reportPart: PropTypes.object
  };

  render() {
    const reportPart = this.props.reportPart;
    const previewData = reportPart.previewData;
    const className = classnames('grant-total', {
      hide: isEmpty(previewData.grandTotalMapping),
      'has-separator': reportPart.reportPartContent.useSeparator
    });
    const reportPartElementMapping = this.getReportPartElementMapping(previewData.grandTotalMapping);

    if (!isEmpty(previewData.grandTotalMapping)) {
      const horizontalElementTypes = this.getHorizontalElementTypes();
      const horizontalRecords = this.getHorizontalRecords(reportPartElementMapping, horizontalElementTypes);
      return this.props.printingMode
        ? this.renderPrintingMode(reportPartElementMapping, horizontalElementTypes, horizontalRecords, className)
        : this.renderNormalMode(reportPartElementMapping, horizontalElementTypes, horizontalRecords, className);
    } else {
      return null;
    }
  }

  renderNormalMode(reportPartElementMapping, horizontalElementTypes, horizontalRecords, className) {
    return (
      <GridLayout.Col className={className}>
        <ReportPartGrandTotalGrid
          records={horizontalRecords}
          {...this.props}
          useFieldNameAlias={true}
          staticRender={this.props.staticRender}
          reportPartElementTypes={horizontalElementTypes}
          reportPartElementMapping={reportPartElementMapping}
        />

        <div className="grand-table grand-table-wrap">
          {this.renderVerticalGrandTotals(reportPartElementMapping, horizontalElementTypes, horizontalRecords)}
        </div>
      </GridLayout.Col>
    );
  }

  renderPrintingMode(reportPartElementMapping, horizontalElementTypes, horizontalRecords, className) {
    const props = this.props;
    const printingClassName = classnames(className, PrintingUtils.getSeparatorClassName(props));

    return (
      <div style={PrintingUtils.getSeparatorContainerStyle(props)}>
        <div className={printingClassName}>
          <ReportPartGrandTotalGrid
            records={horizontalRecords}
            {...this.props}
            useFieldNameAlias={true}
            staticRender={this.props.staticRender}
            reportPartElementTypes={horizontalElementTypes}
          />

          <div className="grand-table">
            {this.renderVerticalGrandTotals(reportPartElementMapping, horizontalElementTypes, horizontalRecords)}
          </div>
        </div>
      </div>
    );
  }

  /**
   * render vertical (right) grand totals
   * @param  [array] reportPartElementMapping
   * @param  [array] verticalElementTypes
   * @return [array]
   */
  renderVerticalGrandTotals(reportPartElementMapping, horizontalElementTypes, horizontalRecords) {
    if (!isNil(reportPartElementMapping)) {
      const previewData = this.props.reportPart.previewData;
      const columns = [];
      let record = {};

      if (!isEmpty(previewData)) {
        record = previewData.records[0];
      }

      // render Total Grand Label for Horizontal values
      columns.push(this.renderHorizontalGrandLabel(horizontalRecords));

      // render vertical rows
      const verticalRows = this.renderVerticalRows(reportPartElementMapping, horizontalElementTypes, record);
      if (!isEmpty(verticalRows)) {
        columns.push(verticalRows);
      }

      return columns;
    }
    return '';
  }

  /**
   * render horizontal Total Grand label
   * @param  [array] horizontalRecords
   * @return [array] array of horizontal Total Grand label
   */
  renderHorizontalGrandLabel(horizontalRecords) {
    const records = [];
    forEach((element, index) => {
      records.push(
        <div className="grand-row" key={`hor-${element.fieldNameAlias}`}>
          <div className="grand-column">
            <div className="grand-cell-content u_paddingAll">{element.grandTotalLabel}</div>
          </div>
        </div>
      );
    }, horizontalRecords);
    return records;
  }

  /**
   * render separator lines
   */
  renderSeparatorLine() {
    return (
      <div className="grand-row">
        {renderBlankLine()}
        {renderBlankLine()}
      </div>
    );
    function renderBlankLine() {
      return (
        <div className="grand-column">
          <div className="grand-cell-content blank-line" />
        </div>
      );
    }
  }

  /**
   * render vertical rows
   * @param  [array] reportPartElementMapping
   * @param  [array] horizontalElementTypes
   * @param  [object] record
   * @return [array] array of vertical rows
   */
  renderVerticalRows(reportPartElementMapping, horizontalElementTypes, record) {
    const verticalRows = [];
    // render vertical Grand Total rows
    Object.keys(reportPartElementMapping).forEach((key, index) => {
      // skip this key if it exists in horizontalElementTypes list
      const isHorizontalType = findIndex(o => o === key, horizontalElementTypes);
      if (isHorizontalType >= 0) {
        return;
      }

      // create separator line
      if (verticalRows.length > 0 && !isEmpty(reportPartElementMapping[key])) {
        verticalRows.push(this.renderSeparatorLine());
      }

      // render vertical columns
      if (!isEmpty(reportPartElementMapping[key])) {
        verticalRows.push(this.renderVerticalColumns(reportPartElementMapping, key, record));
      }
    });

    return !isEmpty(verticalRows) ? (
      <div className="grand-row">
        <div className="grand-column">
          <div className="grand-table">{verticalRows}</div>
        </div>
      </div>
    ) : null;
  }

  /**
   * render vertical columns
   * @param  [array] reportPartElementMapping
   * @param  [object] key
   * @param  [object] record
   * @return [array] array of vertical columns
   */
  renderVerticalColumns(reportPartElementMapping, key, record) {
    // Get grand total content cell style
    const getGrandTotalContentCellStyle = (grandTotalFont: any) => {
      const cellStyle = {
        'fontWeight': (grandTotalFont.bold !== undefined) ?
                        (grandTotalFont.bold ? 'bold' : 'normal') :
                        (calFieldConstants.DEFAULT_VALUE_PROPERTIES.GRAND_TOTAL.FONT.BOLD ? 'bold' : 'normal'),
        'fontStyle': grandTotalFont.italic ? 'italic' : 'normal',
        'textDecoration': grandTotalFont.underline ? 'underline' : 'none',
        'fontFamily': (grandTotalFont.family !== undefined) ? grandTotalFont.family : calFieldConstants.DEFAULT_VALUE_PROPERTIES.FONT.FAMILY,
        'fontSize': (grandTotalFont.size ? grandTotalFont.size : calFieldConstants.DEFAULT_VALUE_PROPERTIES.FONT.SIZE)  + 'px',
        'backgroundColor': grandTotalFont.backgroundColor ? grandTotalFont.backgroundColor : calFieldConstants.DEFAULT_VALUE_PROPERTIES.FONT.BACKGROUND_COLOR,
        'color': grandTotalFont.color ? grandTotalFont.color : calFieldConstants.DEFAULT_VALUE_PROPERTIES.FONT.COLOR
      };
      return cellStyle;
    };
    const renderGrandTotalValue = (grandTotalDataType, grandTotalFormat, colValue) => {
      let value = applyFormatter(grandTotalDataType, grandTotalFormat, colValue);
      if (AppGlobal.isRenderHtmlInReportViewer()){
        value = (<span dangerouslySetInnerHTML={{__html: value}}></span>);
      }
      return value;
    };

    const verticalColumns = [];
    forEach((element, index) => {
      const colValue = record[element.columnName];
      const cellStyle = getGrandTotalContentCellStyle(element.grandTotalFont);
      verticalColumns.push(
        <div className="grand-row" key={`ver-${element.fieldNameAlias}`}>
          <div className="grand-column no-border-left">
            <div className="grand-cell-content">
              {String.format(
                localizer.getText('REPORTDESIGNER_LABEL_GRANDTOTAL_FOR'),
                element.grandTotalLabel,
                element.fieldNameAlias
              )}
            </div>
          </div>
          <div className="grand-column" style={cellStyle}>
            <div className="grand-cell-content">
              {renderGrandTotalValue(element.grandTotalDataType, element.grandTotalFormat, colValue)}
            </div>
          </div>
        </div>
      );
    }, reportPartElementMapping[key]);
    return verticalColumns;
  }

  /**
   * separate grandTotalMappings into 4 types ['columns', 'rows', 'values', 'separators']
   * @param  [array] grandTotalMapping
   * @return [array] array of reportPartElements
   */
  getReportPartElementMapping(grandTotalMapping) {
    const reportPartElementTypes = ['columns', 'rows', 'values', 'separators'];
    const reportPartContent = this.props.reportPart.reportPartContent;
    const reportPartElementMapping = {};

    forEach(elementType => {
      reportPartElementMapping[elementType] = [];
      forEach(mapping => {
        const matchedMapping = find({ name: mapping.fieldNameAlias }, reportPartContent[elementType].visibleElement);
        if (matchedMapping) {
          mapping.grandTotalLabel =
            matchedMapping.properties.dataFormattings.grandTotal.label || mapping.fieldNameAlias;
          mapping.grandTotalFormat = get('properties.dataFormattings.grandTotal.format.format', matchedMapping);
          mapping.grandTotalFont = get('properties.dataFormattings.grandTotal.font', matchedMapping);
          mapping.grandTotalDataType = matchedMapping.properties.dataFormattings.grandTotal.dataType;
          mapping.fieldFormatData = matchedMapping.fieldFormatData;
          mapping.field = matchedMapping.field;
          reportPartElementMapping[elementType].push(mapping);
        }
      }, grandTotalMapping);
    }, reportPartElementTypes);

    return reportPartElementMapping;
  }

  getHorizontalElementTypes() {
    const gridStyle = this.props.reportPart.reportPartContent.properties.generalInfo.gridStyle;
    switch (gridStyle) {
      case constants.GRID_STYLES.VERTICAL.value: // Vertical
        return ['columns'];
      case constants.GRID_STYLES.DRILL_DOWN.value: // Drilldown
        return ['rows', 'values'];
      case constants.GRID_STYLES.PIVOT.value: // Pivot
        return ['rows'];
      case constants.GRID_STYLES.HORIZONTAL.value: // Horizontal
        return ['rows'];
      default:
        return [];
    }
  }

  /**
   * get list of horizontal records
   * @param  [array] reportPartElementMapping
   * @return [array] array of horizontal records
   */
  getHorizontalRecords(reportPartElementMapping, horizontalElementTypes) {
    if (!isNil(reportPartElementMapping)) {
      const previewData = this.props.reportPart.previewData;
      let previewDataFirstRecord = {};
      const records = [];
      if (!isEmpty(previewData)) {
        previewDataFirstRecord = previewData.records[0];
      }

      /**
       * process row data for horizontal:
       *  - group similar Grand Total label in 1 row
       *  - otherwise push in new row
       */
      const processHorizontalRows = type => {
        previewDataFirstRecord &&
          forEach(element => {
            const matchedRow = find(({ grandTotalLabel }) => {
              return grandTotalLabel.toLowerCase() === element.grandTotalLabel.toLowerCase();
            }, records);
            if (matchedRow) {
              // group similar Grand Total label in 1 row
              matchedRow[element.fieldNameAlias] = previewDataFirstRecord[element.columnName];
            } else {
              // push in new row
              const record = {};
              record[element.fieldNameAlias] = previewDataFirstRecord[element.columnName];
              record.grandTotalLabel = element.grandTotalLabel || element.fieldNameAlias;
              record.fieldNameAlias = element.fieldNameAlias;
              records.push(record);
            }
          }, reportPartElementMapping[type]);
      };

      forEach(processHorizontalRows, horizontalElementTypes);

      return records;
    }
    return [];
  }
}



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/ReportBody/ReportPartType/Izenda.GrandTotalGrid.js